'use client';

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { authService } from '@/services/authService';
import { AuthContextType } from '@/types/auth';
import { apiClient } from '@/lib/api-client';
import { useRouter } from 'next/navigation';
import { auth } from '@/lib/firebase';
import { telemetry } from '@/lib/telemetry';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function authErrorMeta(error: unknown) {
  const err = error as { code?: string; message?: string };
  return {
    error_code: err?.code || undefined,
    error_message: err?.message || String(error).slice(0, 300),
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const router = useRouter();
  const isAuthenticating = useRef(false);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (firebaseUser: FirebaseUser | null) => {
      setUser(firebaseUser);
      setLoading(false);
      
      if (firebaseUser) {
        // Only check verification if NOT actively signing up/in
        // This prevents race condition for new signups while still checking for page refreshes
        if (!isAuthenticating.current) {
          try {
            // Call login to check verification AND auto-send email if unverified
            // Safe for page refreshes because user already exists in backend
            // Rate limiting prevents spam even if called multiple times
            const { verified } = await apiClient.login();
            setEmailVerified(verified);
            
            // Redirect to verify-email if not verified
            if (!verified) {
              telemetry.emit('auth.verify_redirect', {
                user_id: firebaseUser.uid,
                step: 'auth_state_changed',
              });
              const isOnVerifyPage = typeof window !== 'undefined' && window.location.pathname.includes('/verify-email');
              if (!isOnVerifyPage) {
                router.push('/verify-email');
              }
            }
          } catch (error) {
            telemetry.emit('auth.verify_error', {
              user_id: firebaseUser.uid,
              step: 'auth_state_changed_login',
              ...authErrorMeta(error),
            });
            setEmailVerified(false);
          }
        }
      } else {
        setEmailVerified(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async (): Promise<{ verified: boolean }> => {
    try {
      setLoading(true);
      isAuthenticating.current = true; // Skip onAuthStateChanged check
      
      // Firebase auth + backend signup
      await authService.signInWithGoogle();
      
      // Now call login (user exists in backend). Backend auto-sends a
      // verification code if the user is unverified (rate-limited to
      // once per 60s on the server).
      const { verified } = await apiClient.login();
      setEmailVerified(verified);
      
      if (!verified) {
        telemetry.emit('auth.verify_redirect', {
          user_id: auth.currentUser?.uid,
          step: 'google_login',
        });
      } else {
        telemetry.emit('auth.success', {
          user_id: auth.currentUser?.uid,
          method: 'google',
          verified: true,
        });
      }
      return { verified };
    } catch (error: any) {
      // Popup closed is a user cancellation, not a real error — emit
      // telemetry but still throw so callers can short-circuit (their
      // catch blocks already special-case `auth/popup-closed-by-user`).
      if (error?.code === 'auth/popup-closed-by-user') {
        telemetry.emit('auth.error', {
          method: 'google',
          stage: 'popup',
          user_cancelled: true,
          ...authErrorMeta(error),
        });
      } else {
        telemetry.emit('auth.error', {
          method: 'google',
          stage: 'login',
          ...authErrorMeta(error),
        });
      }
      throw error;
    } finally {
      isAuthenticating.current = false; // Re-enable checks
      setLoading(false);
    }
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    displayName: string,
  ): Promise<{ verified: boolean }> => {
    try {
      setLoading(true);
      isAuthenticating.current = true; // Skip onAuthStateChanged check
      
      // Firebase auth + backend signup
      await authService.signUpWithEmail(email, password, displayName);
      
      // Now call login (user exists in backend). Backend auto-sends a
      // verification code if the user is unverified.
      const { verified } = await apiClient.login();
      setEmailVerified(verified);
      
      if (!verified) {
        telemetry.emit('auth.verify_redirect', {
          user_id: auth.currentUser?.uid,
          step: 'email_signup',
        });
      } else {
        telemetry.emit('auth.success', {
          user_id: auth.currentUser?.uid,
          method: 'email_signup',
          verified: true,
        });
      }
      return { verified };
    } catch (error) {
      telemetry.emit('auth.error', {
        method: 'email_signup',
        stage: 'signup',
        ...authErrorMeta(error),
      });
      throw error;
    } finally {
      isAuthenticating.current = false; // Re-enable checks
      setLoading(false);
    }
  };

  const signInWithEmail = async (
    email: string,
    password: string,
  ): Promise<{ verified: boolean }> => {
    try {
      setLoading(true);
      isAuthenticating.current = true; // Skip onAuthStateChanged check
      
      // Firebase auth
      await authService.signInWithEmail(email, password);
      
      // Call login to check verification (also auto-sends code if unverified)
      const { verified } = await apiClient.login();
      setEmailVerified(verified);
      
      if (!verified) {
        telemetry.emit('auth.verify_redirect', {
          user_id: auth.currentUser?.uid,
          step: 'email_login',
        });
      } else {
        telemetry.emit('auth.success', {
          user_id: auth.currentUser?.uid,
          method: 'email_login',
          verified: true,
        });
      }
      return { verified };
    } catch (error) {
      telemetry.emit('auth.error', {
        method: 'email_login',
        stage: 'login',
        ...authErrorMeta(error),
      });
      throw error;
    } finally {
      isAuthenticating.current = false; // Re-enable checks
      setLoading(false);
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      await authService.resetPassword(email);
      telemetry.emit('auth.password_reset_success', {
        email_domain: email.includes('@') ? email.split('@').pop() : undefined,
      });
    } catch (error) {
      telemetry.emit('auth.password_reset_error', {
        email_domain: email.includes('@') ? email.split('@').pop() : undefined,
        ...authErrorMeta(error),
      });
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      setLoading(true);
      telemetry.emit('auth.sign_out', { user_id: user?.uid });
      await authService.signOut();
      // Refresh page to ensure complete cleanup of user data
      window.location.reload();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getAuthToken = async (): Promise<string | null> => {
    return authService.getAuthToken();
  };

  const sendVerificationCode = async (): Promise<void> => {
    try {
      await apiClient.sendVerificationCode();
    } catch (error) {
      telemetry.emit('auth.verify_error', {
        user_id: user?.uid,
        step: 'send_code',
        ...authErrorMeta(error),
      });
      throw error;
    }
  };

  const verifyEmailCode = async (code: string): Promise<boolean> => {
    try {
      await apiClient.verifyEmailCode(code);
      setEmailVerified(true);
      telemetry.emit('auth.success', {
        user_id: user?.uid,
        method: 'email_verify_code',
        verified: true,
      });
      return true;
    } catch (error) {
      telemetry.emit('auth.verify_error', {
        user_id: user?.uid,
        step: 'verify_code',
        code_length: code.length,
        ...authErrorMeta(error),
      });
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    emailVerified,
    checkingVerification,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    resetPassword,
    signOut,
    getAuthToken,
    sendVerificationCode,
    verifyEmailCode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
